/**
 * Launch Readiness Dashboard (M10 Phase 4).
 *
 * The executive gate before production launch. Ten pillars scored 0–100, an
 * overall composite, and a Go-Live Checklist that highlights exactly what
 * still needs attention. Refreshes every 20 seconds.
 *
 * P0 pillar = Workspace Isolation. It must read 100 or the launch is
 * blocked and the operator is told so.
 */
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Cpu,
  DollarSign,
  Layers,
  Server,
  ShieldCheck,
  Signal,
  Sparkles,
  Users,
} from "lucide-react";

import {
  getLaunchReadiness,
  type LaunchReadinessResponse,
  type Pillar,
} from "@/entities/launch-readiness";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import { PageHero } from "@/shared/ui/page-hero";

/* ── formatters ───────────────────────────────────────────────────── */
const fmtCurrency = (v: number): string => `$${v.toFixed(v < 1 ? 4 : 2)}`;
const fmtRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

const scoreTone = (score: number): string =>
  score >= 95
    ? "text-emerald-500"
    : score >= 80
    ? "text-sky-500"
    : score >= 60
    ? "text-amber-500"
    : "text-rose-500";

const PILLAR_ORDER = [
  "Workspace Isolation",
  "Security",
  "Billing",
  "AI Providers",
  "Publishing",
  "Infrastructure",
  "Monitoring",
  "Performance",
  "Legal",
  "Deployment",
] as const;

const CHECKLIST_ICONS: Record<string, JSX.Element> = {
  done: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  at_risk: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  todo: <Clock3 className="h-4 w-4 text-rose-500" />,
};

/* ── page ─────────────────────────────────────────────────────────── */
export default function LaunchReadinessPage(): JSX.Element {
  const q = useQuery({
    queryKey: ["launch-readiness"],
    queryFn: () => getLaunchReadiness(),
    refetchInterval: 20_000,
  });

  const data = q.data;
  const overall = data?.overall_score ?? 0;
  const ready = data?.ready_for_launch ?? false;

  return (
    <div
      data-testid="launch-readiness-page"
      className="relative min-h-[calc(100vh-4rem)] overflow-hidden md-anim-fade-in"
    >
      <PageHero
        module="launch"
        title={<>Are we ready<br /> to launch?</>}
        subtitle="Ten pillars, one score. Every red or amber row is a blocker. Workspace isolation is the P0 executive gate — it must hold at 100."
        liveDot
        right={<ScoreCard overall={overall} ready={ready} loading={q.isLoading} />}
      />
      <div className="mx-auto max-w-[1400px] space-y-10 px-6 py-10 md:px-10">
        {/* Executive metrics strip */}
        {data?.metrics ? <MetricsStrip data={data} /> : null}

        {/* Pillars grid */}
        <PillarsGrid pillars={data?.pillars ?? []} loading={q.isLoading} />

        {/* Checklist + errors */}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <GoLiveChecklist items={data?.go_live_checklist ?? []} loading={q.isLoading} />
          <div className="space-y-6">
            <DemoModeCard data={data} />
            <RecentErrors errors={data?.metrics?.recent_errors ?? []} loading={q.isLoading} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── score card (used as PageHero right slot) ──────────────────────── */
function ScoreCard({ overall, ready, loading }: { overall: number; ready: boolean; loading: boolean }): JSX.Element {
  return (
    <div
      data-testid="lr-score-card"
      className="md-panel--glass md-glow rounded-2xl px-8 py-6 text-right"
    >
      <div className="md-eyebrow">Launch Readiness</div>
      <div className={`md-numeric mt-2 text-[72px] leading-none ${scoreTone(overall)}`}>
        {loading ? "…" : overall}
        <span className="ml-2 text-2xl not-italic text-[var(--md-smoke)]">/ 100</span>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2 text-[11px]">
        {ready ? (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--md-good)]" />
            <span className="font-semibold text-[var(--md-good)]">Ready for launch</span>
          </>
        ) : (
          <>
            <AlertTriangle className="h-3.5 w-3.5 text-[var(--md-warn)]" />
            <span className="font-semibold text-[var(--md-warn)]">Blockers remain</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ── metrics ─────────────────────────────────────────────────────── */
function MetricsStrip({ data }: { data: LaunchReadinessResponse }): JSX.Element {
  const m = data.metrics!;
  const tiles = [
    { key: "workspaces", label: "Active Workspaces", value: String(m.active_workspaces), icon: <Layers className="h-4 w-4" />, tone: "text-indigo-500" },
    { key: "users", label: "Active Users", value: String(m.active_users), icon: <Users className="h-4 w-4" />, tone: "text-sky-500" },
    { key: "ai-cost", label: "AI Cost (30d)", value: fmtCurrency(m.ai_cost_month_usd), icon: <DollarSign className="h-4 w-4" />, tone: "text-emerald-500" },
    { key: "ai-req", label: "AI Requests (24h)", value: m.ai_requests_24h.toLocaleString(), icon: <Cpu className="h-4 w-4" />, tone: "text-violet-500" },
    { key: "queue", label: "Queue Depth", value: String(m.queue_depth), icon: <Signal className="h-4 w-4" />, tone: m.queue_depth > 10 ? "text-amber-500" : "text-emerald-500" },
    { key: "dead-letter", label: "Dead-Letter (24h)", value: String(m.dead_letter_24h), icon: <AlertTriangle className="h-4 w-4" />, tone: m.dead_letter_24h > 0 ? "text-rose-500" : "text-emerald-500" },
  ];
  return (
    <div data-testid="lr-metrics" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
      {tiles.map((t) => (
        <div
          key={t.key}
          data-testid={`lr-metric-${t.key}`}
          className="md-panel md-panel--hover rounded-2xl p-5"
        >
          <div className={`flex items-center gap-2 md-eyebrow ${t.tone}`}>
            {t.icon}
            <span>{t.label}</span>
          </div>
          <div className="md-numeric mt-3 text-3xl">{t.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ── pillars ─────────────────────────────────────────────────────── */
function PillarsGrid({ pillars, loading }: { pillars: Pillar[]; loading: boolean }): JSX.Element {
  const sorted = [...pillars].sort(
    (a, b) => PILLAR_ORDER.indexOf(a.pillar as any) - PILLAR_ORDER.indexOf(b.pillar as any),
  );
  if (loading && sorted.length === 0) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }
  return (
    <div data-testid="lr-pillars" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {sorted.map((p) => (
        <PillarCard key={p.pillar} pillar={p} />
      ))}
    </div>
  );
}

function PillarCard({ pillar }: { pillar: Pillar }): JSX.Element {
  const isP0 = pillar.pillar === "Workspace Isolation";
  return (
    <Card
      data-testid={`lr-pillar-${pillar.pillar.toLowerCase().replace(/\s+/g, "-")}`}
      className={`border-border/60 bg-card/70 ${isP0 ? "ring-1 ring-primary/50" : ""}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              {isP0 ? <ShieldCheck className="h-4 w-4 text-primary" /> : null}
              <CardTitle className="text-base">{pillar.pillar}</CardTitle>
            </div>
            {isP0 ? (
              <Badge variant="outline" className="mt-1 font-mono text-[9px] uppercase text-primary">
                P0 · Executive Gate
              </Badge>
            ) : null}
          </div>
          <div className={`font-display text-3xl font-semibold tabular-nums ${scoreTone(pillar.score)}`}>
            {pillar.score}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground leading-relaxed">{pillar.notes}</p>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border/40">
          <div
            className={`h-full transition-all ${
              pillar.score >= 95
                ? "bg-emerald-500"
                : pillar.score >= 80
                ? "bg-sky-500"
                : pillar.score >= 60
                ? "bg-amber-500"
                : "bg-rose-500"
            }`}
            style={{ width: `${pillar.score}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/* ── checklist ───────────────────────────────────────────────────── */
function GoLiveChecklist({
  items,
  loading,
}: {
  items: LaunchReadinessResponse["go_live_checklist"];
  loading: boolean;
}): JSX.Element {
  return (
    <Card data-testid="lr-checklist" className="border-border/60 bg-card/70">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" /> Go-Live Checklist
        </CardTitle>
        <CardDescription>Every green box eliminates one launch risk.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : (
          <ul className="space-y-1.5">
            {(items ?? []).map((item) => (
              <li
                key={item.key}
                data-testid={`lr-check-${item.key}`}
                className="flex items-center justify-between rounded-md border border-border/50 bg-background/60 px-3 py-2.5"
              >
                <div className="flex items-center gap-2">
                  {CHECKLIST_ICONS[item.status]}
                  <span className="text-sm">{item.label}</span>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] uppercase">
                  {item.owner}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ── demo mode ───────────────────────────────────────────────────── */
function DemoModeCard({ data }: { data: LaunchReadinessResponse | undefined }): JSX.Element {
  const enabled = data?.demo_mode?.enabled ?? false;
  return (
    <Card data-testid="lr-demo-mode" className="border-border/60 bg-card/70">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Server className="h-4 w-4 text-fuchsia-500" /> Demo Mode
        </CardTitle>
        <CardDescription>Sandbox behaviours + reversible actions.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className={`h-3 w-3 rounded-full ${enabled ? "bg-amber-500" : "bg-emerald-500"}`}
          />
          <span className="font-mono text-xs uppercase tracking-widest">
            {enabled ? "Enabled" : "Disabled (Production)"}
          </span>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {data?.demo_mode?.note ?? "Demo status not yet reported."}
        </p>
      </CardContent>
    </Card>
  );
}

/* ── errors ──────────────────────────────────────────────────────── */
function RecentErrors({
  errors,
  loading,
}: {
  errors: NonNullable<LaunchReadinessResponse["metrics"]>["recent_errors"];
  loading: boolean;
}): JSX.Element {
  return (
    <Card data-testid="lr-recent-errors" className="border-border/60 bg-card/70">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-amber-500" /> Recent Errors (24h)
        </CardTitle>
        <CardDescription>Last 5 dead-letter events across the platform.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-16" />
        ) : errors.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/60 px-3 py-6 text-center text-xs text-muted-foreground">
            No errors in the last 24 hours. Green across the board.
          </div>
        ) : (
          <ul className="space-y-2 text-xs">
            {errors.map((e, i) => (
              <li
                key={`${e.kind}-${i}`}
                data-testid={`lr-error-${i}`}
                className="rounded-md border border-border/50 bg-background/60 px-3 py-2"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase text-rose-500">{e.kind}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {fmtRelative(e.at)}
                  </span>
                </div>
                <div className="mt-1 truncate">{e.message}</div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
